import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCareerPage, submitCareerApplication } from "../api";
import { localeForLanguage } from "../utils/language";

const API = process.env.REACT_APP_API_URL || "http://localhost:30801";
const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

export default function CareersPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const refParam   = searchParams.get("ref") || "";
  const jobParam   = searchParams.get("job") ? Number(searchParams.get("job")) : null;
  const [expandedJob, setExpandedJob]   = useState<number | null>(jobParam);
  const [applyingJob, setApplyingJob]   = useState<number | null>(null);
  const [submitted, setSubmitted]       = useState<Set<number>>(new Set());
  const [copiedJobId, setCopiedJobId]   = useState<number | null>(null);
  const [search, setSearch]             = useState("");
  const [typeFilter, setTypeFilter]     = useState("");

  useEffect(() => {
    if (jobParam) {
      setTimeout(() => {
        document.getElementById(`job-${jobParam}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 500);
    }
  }, [jobParam]);

  function shareJob(jobId: number) {
    const url = `${window.location.origin}/careers/${slug}?job=${jobId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedJobId(jobId);
      setTimeout(() => setCopiedJobId(null), 2500);
    });
  }

  const { data, isLoading, isError } = useQuery({
    queryKey: ["career-page", slug],
    queryFn: () => getCareerPage(slug!),
    enabled: !!slug,
    retry: false,
  });

  if (isLoading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", border: `4px solid ${TEAL}`, borderTopColor: "transparent", margin: "0 auto 16px", animation: "spin 0.8s linear infinite" }} />
        <div style={{ color: "#64748b" }}>Loading careers…</div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );

  if (isError || !data) return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f8fafc", gap: 12, padding: 24 }}>
      <div style={{ fontSize: 56 }}>🏢</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a" }}>Page not found</div>
      <div style={{ color: "#64748b", textAlign: "center" }}>This company doesn't have an active career page.</div>
    </div>
  );

  const { branding, jobs } = data;

  const locations      = Array.from(new Set(jobs.map((j: any) => j.location).filter(Boolean))) as string[];
  const empTypes       = Array.from(new Set(jobs.map((j: any) => j.employment_type).filter(Boolean))) as string[];
  const uniqueLocCount = locations.length;

  const filtered = jobs.filter((j: any) => {
    const matchSearch = !search ||
      j.title.toLowerCase().includes(search.toLowerCase()) ||
      (j.location || "").toLowerCase().includes(search.toLowerCase()) ||
      (j.required_skills || []).some((s: string) => s.toLowerCase().includes(search.toLowerCase()));
    const matchType = !typeFilter || j.employment_type === typeFilter;
    return matchSearch && matchType;
  });

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "var(--font-sans)" }}>
      <style>{`
        @keyframes fadeIn   { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin     { to { transform: rotate(360deg) } }
        @keyframes countUp  { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .career-job-card    { animation: fadeIn 0.3s ease; transition: box-shadow 0.2s, transform 0.15s; }
        .career-job-card:hover { transform: translateY(-1px); box-shadow: 0 8px 28px rgba(30,58,138,0.1) !important; }
        .career-chip        { transition: background 0.15s, color 0.15s, border-color 0.15s; }
        .career-chip:hover  { border-color: ${NAVY} !important; }
        .process-step       { transition: transform 0.2s, box-shadow 0.2s; }
        .process-step:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(30,58,138,0.12); }

        @media (max-width: 768px) {
          .career-hero-title       { font-size: 24px !important; }
          .career-hero-sub         { font-size: 14px !important; }
          .career-hero-padding     { padding: 36px 16px 28px !important; }
          .career-why-grid         { grid-template-columns: 1fr !important; }
          .career-process-grid     { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
          .career-stats-grid       { grid-template-columns: repeat(2, 1fr) !important; }
          .career-job-meta         { flex-direction: column !important; gap: 4px !important; }
          .career-apply-grid       { grid-template-columns: 1fr !important; }
          .career-skills-section   { grid-template-columns: 1fr !important; }
          .career-job-header       { flex-direction: column !important; gap: 10px !important; }
          .career-job-actions      { justify-content: flex-start !important; }
          .career-filter-row       { flex-direction: column !important; gap: 8px !important; }
        }
        @media (max-width: 480px) {
          .career-hero-title       { font-size: 21px !important; }
          .career-process-grid     { grid-template-columns: 1fr !important; }
          .career-stats-grid       { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* ── Hero ── */}
      <div className="career-hero-padding" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #1d4ed8 45%, ${TEAL} 100%)`, padding: "56px 24px 44px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, opacity: 0.06, backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div style={{ position: "absolute", top: -60, right: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -40, left: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 700, margin: "0 auto", position: "relative" }}>
          {branding.logo_url && (
            <img src={`${API}${branding.logo_url}`} alt={branding.company_name}
              style={{ height: 56, maxWidth: 200, objectFit: "contain", marginBottom: 20, borderRadius: 8, background: "rgba(255,255,255,0.15)", padding: "6px 12px" }}
              onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
          )}
          <h1 className="career-hero-title" style={{ color: "white", fontSize: 36, fontWeight: 800, margin: "0 0 10px", lineHeight: 1.2, letterSpacing: "-0.5px" }}>
            Join {branding.company_name}
          </h1>
          {branding.tagline && (
            <p className="career-hero-sub" style={{ color: "rgba(255,255,255,0.88)", fontSize: 17, margin: "0 0 20px", lineHeight: 1.6, maxWidth: 520, marginLeft: "auto", marginRight: "auto" }}>
              {branding.tagline}
            </p>
          )}
          <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
            {branding.location && <HeroPill icon="📍">{branding.location}</HeroPill>}
            {branding.website && (
              <a href={branding.website} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                <HeroPill icon="🌐">{branding.website.replace(/^https?:\/\//, "")}</HeroPill>
              </a>
            )}
            <HeroPill icon="💼">{jobs.length} open position{jobs.length !== 1 ? "s" : ""}</HeroPill>
          </div>
        </div>
      </div>

      {/* ── Jobs section ── */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 16px 0" }}>

        {/* Search + filters */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ position: "relative", marginBottom: 10 }}>
            <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "#94a3b8" }}>🔍</span>
            <input
              placeholder="Search by title, location, or skill…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: "100%", padding: "12px 14px 12px 40px", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", boxSizing: "border-box", background: "white", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
            />
          </div>

          {/* Location chips */}
          {locations.length > 1 && (
            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "#94a3b8", alignSelf: "center", marginRight: 2 }}>Location:</span>
              {(["", ...locations] as string[]).map(loc => (
                <button key={loc || "__all__"} onClick={() => setSearch(loc)} className="career-chip"
                  style={{ padding: "4px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 500,
                    border: `1px solid ${(!loc && !search) || search === loc ? NAVY : "#e2e8f0"}`,
                    background: (!loc && !search) || search === loc ? NAVY : "white",
                    color: (!loc && !search) || search === loc ? "white" : "#64748b" }}>
                  {loc ? `📍 ${loc}` : "All"}
                </button>
              ))}
            </div>
          )}

          {/* Employment type chips */}
          {empTypes.length > 1 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, color: "#94a3b8", alignSelf: "center", marginRight: 2 }}>Type:</span>
              {(["", ...empTypes] as string[]).map(t => (
                <button key={t || "__all__"} onClick={() => setTypeFilter(t)} className="career-chip"
                  style={{ padding: "4px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 500,
                    border: `1px solid ${typeFilter === t ? NAVY : "#e2e8f0"}`,
                    background: typeFilter === t ? NAVY : "white",
                    color: typeFilter === t ? "white" : "#64748b" }}>
                  {t || "All"}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Job count */}
        <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
          {filtered.length === 0 && (search || typeFilter)
            ? `No jobs match your filters`
            : `${filtered.length} open position${filtered.length !== 1 ? "s" : ""}${search ? ` matching "${search}"` : ""}${typeFilter ? ` · ${typeFilter}` : ""}`}
        </div>

        {/* No jobs empty state */}
        {jobs.length === 0 && (
          <div style={{ textAlign: "center", padding: "56px 20px", background: "white", borderRadius: 16, border: "1px solid #f1f5f9" }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>📭</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 8 }}>No open positions right now</div>
            <div style={{ fontSize: 14, color: "#64748b", maxWidth: 340, margin: "0 auto 24px", lineHeight: 1.7 }}>
              We're not actively hiring at the moment, but we're always growing. Check back soon!
            </div>
            {branding.website && (
              <a href={branding.website} target="_blank" rel="noreferrer"
                style={{ display: "inline-block", background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white", textDecoration: "none", borderRadius: 9, padding: "11px 24px", fontSize: 14, fontWeight: 700 }}>
                Visit our website →
              </a>
            )}
          </div>
        )}

        {/* No filter results */}
        {jobs.length > 0 && filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px", background: "white", borderRadius: 16, border: "1px solid #f1f5f9" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔎</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>No matching positions</div>
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>Try a different search or clear your filters.</div>
            <button onClick={() => { setSearch(""); setTypeFilter(""); }}
              style={{ background: NAVY, color: "white", border: "none", borderRadius: 8, padding: "9px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Clear filters
            </button>
          </div>
        )}

        {/* Job cards */}
        {filtered.map((job: any) => {
          const isExpanded = expandedJob === job.id;
          const isApplying = applyingJob === job.id;
          const isDone     = submitted.has(job.id);
          return (
            <div key={job.id} id={`job-${job.id}`} className="career-job-card" lang={localeForLanguage(job.language)} style={{
              background: "white", borderRadius: 14,
              border: `1px solid ${isExpanded ? "#bfdbfe" : "#e2e8f0"}`,
              marginBottom: 12, overflow: "hidden",
              boxShadow: isExpanded ? "0 6px 24px rgba(30,58,138,0.10)" : "0 1px 4px rgba(0,0,0,0.04)",
            }}>
              {/* Job header */}
              <div className="career-job-header" onClick={() => !isApplying && setExpandedJob(isExpanded ? null : job.id)}
                style={{ padding: "18px 20px", cursor: "pointer", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{job.title}</h3>
                    {isDone && <span style={{ background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20 }}>✓ Applied</span>}
                  </div>
                  <div className="career-job-meta" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {job.location && <MetaTag icon="📍">{job.location}</MetaTag>}
                    {job.employment_type && <MetaTag icon="💼">{job.employment_type}</MetaTag>}
                    {job.experience_years && <MetaTag icon="⏱">{job.experience_years}</MetaTag>}
                    {job.budget && job.budget.toLowerCase() !== "competitive" && <MetaTag icon="💰">{job.budget}</MetaTag>}
                  </div>
                </div>
                <div className="career-job-actions" style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {!isDone && (
                    <button onClick={e => { e.stopPropagation(); setApplyingJob(job.id); setExpandedJob(job.id); }}
                      style={{ background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                      Apply Now
                    </button>
                  )}
                  <button onClick={e => { e.stopPropagation(); shareJob(job.id); }}
                    style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 7, padding: "7px 12px", cursor: "pointer", fontSize: 12, color: copiedJobId === job.id ? "#16a34a" : "#94a3b8", whiteSpace: "nowrap" }}>
                    {copiedJobId === job.id ? "✓ Copied" : "🔗 Share"}
                  </button>
                  <span style={{ color: "#94a3b8", fontSize: 20, transition: "transform 0.2s", transform: isExpanded ? "rotate(90deg)" : "none", display: "inline-block" }}>›</span>
                </div>
              </div>

              {/* Expanded */}
              {isExpanded && (
                <div style={{ borderTop: "1px solid #f1f5f9", padding: "20px 20px 24px", animation: "fadeIn 0.2s ease" }}>

                  {!isApplying && !isDone && (
                    <>
                      {job.summary && (
                        <p style={{ fontSize: 14, color: "#334155", lineHeight: 1.75, marginBottom: 20, marginTop: 0 }}>{job.summary}</p>
                      )}

                      <div className="career-skills-section" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
                        {job.required_skills.length > 0 && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Required Skills</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                              {job.required_skills.map((s: string) => (
                                <span key={s} style={{ background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", padding: "4px 11px", borderRadius: 20, fontSize: 12, fontWeight: 500 }}>{s}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {job.nice_to_have.length > 0 && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>Nice to Have</div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                              {job.nice_to_have.map((s: string) => (
                                <span key={s} style={{ background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", padding: "4px 11px", borderRadius: 20, fontSize: 12 }}>{s}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {job.responsibilities.length > 0 && (
                        <div style={{ marginBottom: 20 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Responsibilities</div>
                          <ul style={{ margin: 0, paddingLeft: 18, color: "#334155", fontSize: 13, lineHeight: 2 }}>
                            {job.responsibilities.map((r: string, i: number) => <li key={i}>{r}</li>)}
                          </ul>
                        </div>
                      )}

                      <button onClick={() => setApplyingJob(job.id)}
                        style={{ background: `linear-gradient(135deg, ${NAVY}, ${TEAL})`, color: "white", border: "none", borderRadius: 9, padding: "12px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", width: "100%" }}>
                        Apply for this Position →
                      </button>
                    </>
                  )}

                  {isApplying && !isDone && (
                    <ApplyForm
                      jobTitle={job.title}
                      screeningQuestions={job.screening_questions || []}
                      refCode={refParam}
                      onSubmit={async (fd) => {
                        await submitCareerApplication(slug!, job.id, fd);
                        setSubmitted(s => new Set(s).add(job.id));
                        setApplyingJob(null);
                      }}
                      onCancel={() => setApplyingJob(null)}
                    />
                  )}

                  {isDone && (
                    <div style={{ textAlign: "center", padding: "32px 0" }}>
                      <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>Application submitted!</div>
                      <div style={{ fontSize: 13, color: "#64748b" }}>We'll review your CV and reach out if you're a great fit.</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Don't see your role? */}
        {jobs.length > 0 && (
          <div style={{ background: `linear-gradient(135deg, ${NAVY}08 0%, ${TEAL}12 100%)`, border: `1px solid ${NAVY}20`, borderRadius: 16, padding: "28px 24px", textAlign: "center", marginTop: 8 }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>🌟</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>Don't see the right role?</div>
            <div style={{ fontSize: 13, color: "#64748b", maxWidth: 380, margin: "0 auto 16px", lineHeight: 1.7 }}>
              We're always interested in exceptional talent. Reach out directly and tell us how you'd like to contribute.
            </div>
            {branding.website && (
              <a href={branding.website} target="_blank" rel="noreferrer"
                style={{ display: "inline-block", background: NAVY, color: "white", textDecoration: "none", borderRadius: 8, padding: "10px 22px", fontSize: 13, fontWeight: 700 }}>
                Get in touch →
              </a>
            )}
          </div>
        )}
      </div>

      {/* ── About section divider ── */}
      <div style={{ maxWidth: 760, margin: "40px auto 0", padding: "0 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
          <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap" }}>
            About {branding.company_name}
          </span>
          <div style={{ flex: 1, height: 1, background: "#e2e8f0" }} />
        </div>
      </div>

      {/* ── Stats Bar ── */}
      {jobs.length > 0 && (
        <div style={{ background: "white", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", marginTop: 20 }}>
          <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 20px" }}>
            <div className="career-stats-grid" style={{ display: "grid", gridTemplateColumns: `repeat(${[true, uniqueLocCount > 0, empTypes.length > 0, true].filter(Boolean).length}, 1fr)` }}>
              <StatCell value={jobs.length} label="Open Positions" icon="💼" />
              {uniqueLocCount > 0 && <StatCell value={uniqueLocCount} label={uniqueLocCount === 1 ? "Location" : "Locations"} icon="📍" />}
              {empTypes.length > 0 && <StatCell value={empTypes.length} label="Work Types" icon="🕐" />}
              <StatCell value="100%" label="Application Review" icon="✅" />
            </div>
          </div>
        </div>
      )}

      {/* ── Why Join Us ── */}
      <div style={{ background: "white", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "36px 20px" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: "0 0 6px", letterSpacing: "-0.3px" }}>Why work with us?</h2>
            <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>We believe great work happens when great people come together.</p>
          </div>
          <div className="career-why-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
            {[
              { icon: "🚀", title: "Growth", desc: "Clear career paths, mentorship, and continuous learning to help you reach your potential" },
              { icon: "🤝", title: "Culture", desc: "Collaborative, inclusive environment where every voice matters and ideas are welcomed" },
              { icon: "⚡", title: "Impact", desc: "Work on meaningful projects that create real value for people and businesses" },
              { icon: "🏡", title: "Flexibility", desc: "Flexible work arrangements designed around your life, not the other way around" },
              { icon: "🎯", title: "Purpose", desc: "Clear goals, transparent decisions, and leadership that supports your success" },
              { icon: "🌍", title: "Diversity", desc: "Diverse teams with varied perspectives that lead to better ideas and outcomes" },
            ].map(p => (
              <div key={p.title} className="process-step" style={{ textAlign: "center", padding: "22px 16px", background: "#f8fafc", borderRadius: 14, border: "1px solid #f1f5f9", cursor: "default" }}>
                <div style={{ fontSize: 34, marginBottom: 10 }}>{p.icon}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>{p.title}</div>
                <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6 }}>{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── How We Hire ── */}
      <div style={{ background: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "36px 20px" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", margin: "0 0 6px", letterSpacing: "-0.3px" }}>How we hire</h2>
            <p style={{ color: "#64748b", fontSize: 14, margin: 0 }}>A transparent, respectful process from first click to first day.</p>
          </div>
          <div className="career-process-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
            {[
              { step: "01", icon: "📝", title: "Apply",     desc: "Submit your CV and a short note. Takes 2 minutes." },
              { step: "02", icon: "🔍", title: "Review",    desc: "Our team reviews every application within 5 business days." },
              { step: "03", icon: "💬", title: "Interview", desc: "A friendly conversation to learn about you and your experience." },
              { step: "04", icon: "🎉", title: "Offer",     desc: "Fast decisions and a clear, fair offer — no surprises." },
            ].map((s, i) => (
              <div key={i} className="process-step" style={{ background: "white", borderRadius: 14, border: "1px solid #e2e8f0", padding: "20px 14px", textAlign: "center", position: "relative", cursor: "default" }}>
                <div style={{ position: "absolute", top: 10, right: 12, fontSize: 11, fontWeight: 800, color: "#cbd5e1", letterSpacing: "0.05em" }}>{s.step}</div>
                <div style={{ fontSize: 30, marginBottom: 10 }}>{s.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>{s.title}</div>
                <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{ textAlign: "center", padding: "24px 16px", background: "#f8fafc" }}>
        <div style={{ fontSize: 12, color: "#cbd5e1" }}>
          Powered by <span style={{ color: "#94a3b8", fontWeight: 600 }}>HRxAI</span>
        </div>
      </div>
    </div>
  );
}

// ── Apply Form ────────────────────────────────────────────────────────────────

interface ScreeningQuestion {
  id: number;
  label: string;
  field_type: "text" | "number" | "single_select" | "multi_select" | "yes_no";
  options: string[];
  required: boolean;
}

function ApplyForm({ jobTitle, refCode, screeningQuestions, onSubmit, onCancel }: {
  jobTitle: string;
  refCode?: string;
  screeningQuestions: ScreeningQuestion[];
  onSubmit: (fd: FormData) => Promise<void>;
  onCancel: () => void;
}) {
  const [form, setForm]     = useState({ name: "", email: "", phone: "", cover_note: "" });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");
  const [answers, setAnswers] = useState<Record<number, any>>({});

  function setAnswer(qid: number, value: any) {
    setAnswers(a => ({ ...a, [qid]: value }));
  }

  function isBlank(v: any) {
    return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!cvFile) { setError("Please attach your CV"); return; }
    if (!consent) { setError("Please tick the consent checkbox to proceed"); return; }
    for (const q of screeningQuestions) {
      if (q.required && isBlank(answers[q.id])) {
        setError(`Please answer: ${q.label}`);
        return;
      }
    }
    setLoading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("cover_note", form.cover_note);
      if (refCode) fd.append("ref", refCode);
      fd.append("pdpa_consent", "true");
      fd.append("cv", cvFile);
      const screeningAnswers = screeningQuestions
        .filter(q => !isBlank(answers[q.id]))
        .map(q => ({ question_id: q.id, value: answers[q.id] }));
      if (screeningAnswers.length) fd.append("screening_answers", JSON.stringify(screeningAnswers));
      await onSubmit(fd);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Apply: {jobTitle}</div>
        <button type="button" onClick={onCancel} style={{ background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 18 }}>✕</button>
      </div>

      <div className="career-apply-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Full Name *</label>
          <input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Your full name" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Email *</label>
          <input required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="you@example.com" style={inputStyle} />
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Phone (optional)</label>
        <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
          placeholder="+1 234 567 8900" style={inputStyle} />
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Why are you a great fit? (optional)</label>
        <textarea value={form.cover_note} onChange={e => setForm(f => ({ ...f, cover_note: e.target.value }))}
          rows={3} placeholder="Brief introduction and why you're excited about this role…"
          style={{ ...inputStyle, resize: "vertical" }} />
      </div>

      {screeningQuestions.map(q => (
        <div key={q.id} style={{ marginBottom: 12 }}>
          <ScreeningQuestionField question={q} value={answers[q.id]} onChange={v => setAnswer(q.id, v)} />
        </div>
      ))}

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>CV / Resume * <span style={{ color: "#94a3b8", fontWeight: 400 }}>(PDF or Word, max 10 MB)</span></label>
        <label style={{
          display: "flex", alignItems: "center", gap: 12, cursor: "pointer",
          border: `2px dashed ${cvFile ? TEAL : "#e2e8f0"}`,
          borderRadius: 10, padding: "16px 18px",
          background: cvFile ? "#f0fdf4" : "#f8fafc",
          transition: "all 0.2s",
        }}>
          <input type="file" accept=".pdf,.doc,.docx" style={{ display: "none" }}
            onChange={e => setCvFile(e.target.files?.[0] || null)} />
          <span style={{ fontSize: 26 }}>{cvFile ? "✅" : "📎"}</span>
          <div>
            <div style={{ fontSize: 13, color: cvFile ? "#166534" : "#334155", fontWeight: 600 }}>
              {cvFile ? cvFile.name : "Click to attach your CV"}
            </div>
            {!cvFile && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>PDF, DOC, or DOCX up to 10 MB</div>}
          </div>
        </label>
      </div>

      {/* PDPA Consent */}
      <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 16, padding: "12px 14px", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 9 }}>
        <input
          type="checkbox"
          checked={consent}
          onChange={e => setConsent(e.target.checked)}
          style={{ marginTop: 2, flexShrink: 0, accentColor: NAVY, width: 15, height: 15 }}
        />
        <span style={{ fontSize: 12, color: "#475569", lineHeight: 1.6 }}>
          I consent to this company collecting, storing, and using my personal data (including this CV) for recruitment purposes.
          My data may be retained for up to 2 years. I understand I can request deletion at any time by contacting the company directly.{" "}
          <span style={{ color: "#94a3b8" }}>(Required under PDPA)</span>
        </span>
      </label>

      {error && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", color: "#991b1b", fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button type="submit" disabled={loading || !consent} style={{
          flex: 1, background: (loading || !consent) ? "#94a3b8" : `linear-gradient(135deg, ${NAVY}, ${TEAL})`,
          color: "white", border: "none", borderRadius: 9, padding: "12px 0",
          fontSize: 14, fontWeight: 700, cursor: (loading || !consent) ? "not-allowed" : "pointer",
          opacity: !consent ? 0.65 : 1,
        }}>
          {loading ? "Submitting…" : "Submit Application →"}
        </button>
        <button type="button" onClick={onCancel}
          style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 9, padding: "12px 20px", fontSize: 14, cursor: "pointer", color: "#64748b" }}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ScreeningQuestionField({ question, value, onChange }: {
  question: ScreeningQuestion;
  value: any;
  onChange: (v: any) => void;
}) {
  const label = `${question.label}${question.required ? " *" : ""}`;

  if (question.field_type === "text") {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input value={value || ""} onChange={e => onChange(e.target.value)} style={inputStyle} />
      </>
    );
  }
  if (question.field_type === "number") {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input type="number" value={value ?? ""} onChange={e => onChange(e.target.value)} style={inputStyle} />
      </>
    );
  }
  if (question.field_type === "single_select") {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <select value={value || ""} onChange={e => onChange(e.target.value)} style={inputStyle}>
          <option value="">— select —</option>
          {question.options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </>
    );
  }
  if (question.field_type === "yes_no") {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <select value={value || ""} onChange={e => onChange(e.target.value)} style={inputStyle}>
          <option value="">— select —</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </>
    );
  }
  // multi_select
  const selected: string[] = Array.isArray(value) ? value : [];
  function toggle(o: string) {
    onChange(selected.includes(o) ? selected.filter(v => v !== o) : [...selected, o]);
  }
  return (
    <>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {question.options.map(o => (
          <label key={o} style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155",
            border: `1px solid ${selected.includes(o) ? TEAL : "#e2e8f0"}`, borderRadius: 20,
            padding: "5px 12px", cursor: "pointer", background: selected.includes(o) ? "#f0fdf4" : "white",
          }}>
            <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} style={{ margin: 0 }} />
            {o}
          </label>
        ))}
      </div>
    </>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function StatCell({ value, label, icon }: { value: number | string; label: string; icon: string }) {
  return (
    <div style={{ padding: "18px 12px", textAlign: "center", borderRight: "1px solid #f1f5f9", animation: "countUp 0.4s ease" }}>
      <div style={{ fontSize: 11, marginBottom: 4 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: NAVY, letterSpacing: "-0.5px" }}>{value}</div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{label}</div>
    </div>
  );
}


function HeroPill({ icon, children }: { icon: string; children?: React.ReactNode }) {
  return (
    <span style={{ background: "rgba(255,255,255,0.18)", color: "white", backdropFilter: "blur(4px)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 20, padding: "5px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 5 }}>
      {icon} {children}
    </span>
  );
}

function MetaTag({ icon, children }: { icon: string; children?: React.ReactNode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "#64748b" }}>
      <span>{icon}</span>{children}
    </span>
  );
}

const labelStyle: React.CSSProperties = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 5,
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db",
  fontSize: 13, color: "#334155", outline: "none", boxSizing: "border-box",
  background: "white",
};
